import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';

/**
 * Compact timer widget. Two modes:
 *   - Timer: start → counts up → stop creates a TimeEntry with elapsed mins
 *   - Manual: just minutes + description fields
 * Embedded on ClientDetail/case rows; could also live on a future
 * CaseDetail page.
 */
export function TimeEntryWidget({
  caseId,
  caseLabel,
  onSaved,
}: {
  caseId: string;
  caseLabel: string;
  onSaved?: () => void;
}) {
  const { t } = useTranslation(['billing', 'common']);
  const [mode, setMode] = useState<'timer' | 'manual'>('timer');
  const [running, setRunning] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const startRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [manualMinutes, setManualMinutes] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  function startTimer() {
    startRef.current = Date.now();
    setElapsedSec(0);
    setRunning(true);
    intervalRef.current = setInterval(() => {
      if (startRef.current) {
        setElapsedSec(Math.floor((Date.now() - startRef.current) / 1000));
      }
    }, 1000);
  }

  async function stopTimer() {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setRunning(false);
    const minutes = Math.max(1, Math.round(elapsedSec / 60));
    await save(minutes, description, startRef.current ? new Date(startRef.current) : null);
    setElapsedSec(0);
    startRef.current = null;
  }

  async function saveManual() {
    const minutes = Number(manualMinutes);
    if (!Number.isFinite(minutes) || minutes <= 0) return;
    await save(minutes, description, null);
    setManualMinutes('');
  }

  async function save(minutes: number, desc: string, startedAt: Date | null) {
    setBusy(true);
    setError(null);
    setSavedNote(null);
    try {
      const body: Record<string, unknown> = {
        caseId,
        minutes,
        billable: true,
      };
      if (desc.trim()) body['description'] = desc.trim();
      if (startedAt) body['startedAt'] = startedAt.toISOString();

      await apiFetch('/api/time-entries', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setDescription('');
      setSavedNote(t('timer.saved', { minutes }));
      onSaved?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const mm = String(Math.floor(elapsedSec / 60)).padStart(2, '0');
  const ss = String(elapsedSec % 60).padStart(2, '0');

  return (
    <div className="timer-widget">
      <div className="timer-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="section-label" style={{ marginBottom: 0 }}>{t('timer.title')}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{caseLabel}</div>
        </div>
        <div className="timer-mode-toggle">
          <button
            type="button"
            className={`type-btn ${mode === 'timer' ? 'active' : ''}`}
            onClick={() => setMode('timer')}
            style={{ flex: 'unset', padding: '4px 10px', fontSize: 11 }}
          >
            ⏱
          </button>
          <button
            type="button"
            className={`type-btn ${mode === 'manual' ? 'active' : ''}`}
            onClick={() => setMode('manual')}
            style={{ flex: 'unset', padding: '4px 10px', fontSize: 11 }}
          >
            ✎
          </button>
        </div>
      </div>

      {mode === 'timer' ? (
        <div className="timer-body">
          <div className="timer-display">
            {mm}:{ss}
          </div>
          {!running ? (
            <button type="button" className="btn-save" onClick={startTimer} disabled={busy}>
              {t('timer.start')}
            </button>
          ) : (
            <button type="button" className="btn-danger" onClick={stopTimer} disabled={busy}>
              {t('timer.stop')}
            </button>
          )}
        </div>
      ) : (
        <div className="timer-body" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
          <input
            type="number"
            placeholder={t('timer.minutes')}
            value={manualMinutes}
            onChange={(e) => setManualMinutes(e.target.value)}
            min={1}
            max={1440}
            style={{ padding: '8px 10px', border: '1px solid var(--border2)', borderRadius: 8 }}
          />
        </div>
      )}

      <input
        placeholder={t('timer.description')}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        style={{ padding: '8px 10px', border: '1px solid var(--border2)', borderRadius: 8, marginTop: 8, width: '100%' }}
      />

      {mode === 'manual' && (
        <button
          type="button"
          className="btn-save"
          onClick={saveManual}
          disabled={busy || !manualMinutes}
          style={{ marginTop: 8 }}
        >
          {t('timer.save')}
        </button>
      )}

      {savedNote && <div className="timer-saved-note">{savedNote}</div>}
      {error && <div className="signin-error" style={{ marginTop: 6, fontSize: 11 }}>{error}</div>}
    </div>
  );
}
